import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { TestId } from '@modules/utilities/constants/testConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { useTranslation } from '@rbx/intl';
import { LinkIcon, Typography, makeStyles } from '@rbx/ui';
import React, {
  ComponentPropsWithoutRef,
  FunctionComponent,
  MouseEventHandler,
  useCallback,
} from 'react';
import { getCustomMarkdownFontSize } from '@modules/layout/layout/constants/layoutConstants';

export type HeaderProps =
  | ComponentPropsWithoutRef<'h1'>
  | ComponentPropsWithoutRef<'h2'>
  | ComponentPropsWithoutRef<'h3'>
  | ComponentPropsWithoutRef<'h4'>
  | ComponentPropsWithoutRef<'h5'>
  | ComponentPropsWithoutRef<'h6'>;

export interface MarkdownHeaderProps extends HeaderProps {
  variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const useStyles = makeStyles<
  { variant: string; enableEngineReferenceRevamp: boolean },
  'copyIcon'
>()((theme, { variant, enableEngineReferenceRevamp }, { copyIcon }) => {
  // Font size overrides for specific header variants
  // h3 is overridden in globals.css
  // h4 and h5 should be same as body
  const headerFontSizes: Record<string, string> = {
    h4: getCustomMarkdownFontSize(enableEngineReferenceRevamp),
    h5: getCustomMarkdownFontSize(enableEngineReferenceRevamp),
  };

  return {
    root: {
      '&:hover $copyAnchor': {
        visibility: 'visible',
      },
    },
    headerFontSizeOverride: {
      ...(headerFontSizes[variant] && { fontSize: headerFontSizes[variant] }),
    },
    copyAnchor: {
      visibility: 'hidden',
      marginLeft: '0.5em',
      [`&:hover ${copyIcon}`]: {
        color: 'inherit',
      },
    },
    copyIcon: {
      color: theme.palette.states.disabled,
      verticalAlign: 'middle',
    },
  };
});

const MarkdownHeader: FunctionComponent<React.PropsWithChildren<MarkdownHeaderProps>> = ({
  variant,
  children,
  className,
  ...otherProps
}) => {
  const { translate } = useTranslation();
  const { enableEngineReferenceRevamp } = useAppConfig();
  const { id } = otherProps;
  const {
    classes: { root, copyAnchor, copyIcon, headerFontSizeOverride },

    cx,
  } = useStyles({ variant, enableEngineReferenceRevamp: !!enableEngineReferenceRevamp });
  const href = `#${id}`;
  const handleCopy = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(e.currentTarget.href);
  }, []);
  const content = id
    ? [
        <DynamicallyRouted.Link
          href={href}
          key='heading'
          onClick={handleCopy}
          data-testid={TestId.CopyHeadingLink}
          aria-label={translate('Label.CopyHeaderLink')}
          color='inherit'
          underline='none'>
          {children}
        </DynamicallyRouted.Link>,
        <DynamicallyRouted.Link
          key='copy-icon'
          href={href}
          data-testid={TestId.CopyHeadingLink}
          aria-label={translate('Label.CopyHeaderLink')}
          onClick={handleCopy}
          classes={{ root: copyAnchor }}
          color='inherit'
          underline='none'>
          <LinkIcon classes={{ root: copyIcon }} />
        </DynamicallyRouted.Link>,
      ]
    : children;
  return (
    <Typography
      {...otherProps}
      classes={{ root: cx(className, root, headerFontSizeOverride) }}
      color='primary'
      variant={variant}
      component={variant}
      paragraph>
      {content}
    </Typography>
  );
};

export default MarkdownHeader;
